// Hooks
import { useAuth } from "./useAuth";

// Utils
import { UserRole } from "../utils/consts";
import { User } from "../utils/types";
import { doesUserHaveRoles } from "../utils/helpers";

interface usePermissionProps {
	/** The current user logged in */
	user: User | undefined;
	/** The current user's roles */
	roles: UserRole[];
	/** If the current user has general manager-level admin portal access */
	hasGeneralManagerAccess: boolean;
	/** If the current user has general admin-level admin portal access, this is lower privileges than manager */
	hasGeneralAdminAccess: boolean;
	/** If the current user has general claims portal access */
	hasGeneralClaimsAccess: boolean;
	/** If the current user has general update claims portal access */
	hasGeneralUpdateAccess: boolean;
	/** If the current user has limited update claims portal access, like creating notes */
	hasLimitedUpdateAccess: boolean;
	/** If the current user has access to export payments */
	hasPaymentsExportAccess: boolean;
	/** If the current user has general network-level admin portal access */
	hasGeneralNetworkAccess: boolean;
	isProvider: boolean;
}

/** A hook used to handle UI permissions for the current user */
const usePermission = (): usePermissionProps => {
	const { getUser } = useAuth();
	const user = getUser();
	const roles = (user?.role?.split(",") as UserRole[]) ?? [];

	const hasGeneralManagerAccess = roles.includes(UserRole.ClaimsManager);

	const hasGeneralAdminAccess =
		roles.includes(UserRole.ClaimsAdmin) ||
		roles.includes(UserRole.ClaimsManager) ||
		roles.includes(UserRole.ClaimsAnalyst);

	const hasGeneralClaimsAccess =
		roles.includes(UserRole.ClaimsAdmin) ||
		roles.includes(UserRole.ClaimsAnalyst) ||
		roles.includes(UserRole.ClaimsManager) ||
		roles.includes(UserRole.ClaimsViewOnly) ||
		roles.includes(UserRole.ClaimsNetworkManager) ||
		roles.includes(UserRole.Payments);

	const hasGeneralUpdateAccess = roles.includes(UserRole.ClaimsAnalyst) || roles.includes(UserRole.ClaimsManager);

	const hasGeneralNetworkAccess =
		roles.includes(UserRole.ClaimsNetworkManager) || roles.includes(UserRole.ClaimsManager);

	const hasLimitedUpdateAccess =
		roles.includes(UserRole.ClaimsAdmin) ||
		roles.includes(UserRole.ClaimsAnalyst) ||
		roles.includes(UserRole.ClaimsManager) ||
		roles.includes(UserRole.Payments);

	const hasPaymentsExportAccess = roles.includes(UserRole.ClaimsAdmin) || roles.includes(UserRole.Payments);

	const isProvider = (user && doesUserHaveRoles(user, [UserRole.Provider])) || false;

	return {
		user,
		roles,
		hasGeneralManagerAccess,
		hasGeneralAdminAccess,
		hasGeneralClaimsAccess,
		hasGeneralUpdateAccess,
		hasLimitedUpdateAccess,
		hasPaymentsExportAccess,
		hasGeneralNetworkAccess,
		isProvider
	};
};

export default usePermission;
